<template>
  <div class="notification is-info is-light signing-link-panel">
    <div class="columns is-mobile">
      <div class="column is-narrow">
        <img :src="qrCode" width="180" height="180" alt="Signing link QR code" v-if="qrCode">
      </div>
      <div class="column">
        <p class="mb-5"><strong>{{ waitingText }}</strong></p>
        <p class="mb-10">Scan the code with the iPad camera, or copy or text the link to it. The link can be used once and expires at {{ expiryText }}.</p>
        <div class="field has-addons">
          <div class="control is-expanded">
            <input class="input is-small" type="text" readonly :value="link.url">
          </div>
          <div class="control">
            <button type="button" class="button is-small signing-link-action" v-clipboard:copy="link.url" @click="successToast('Link copied.')">Copy</button>
          </div>
        </div>
        <div class="field has-addons">
          <div class="control is-expanded">
            <b-autocomplete
              size="is-small"
              type="tel"
              placeholder="Number to text the link to (the signer's mobile by default)"
              v-model="textMobile"
              :data="numberOptions"
              field="number"
              :open-on-focus="true"
              :expanded="true">
              <template slot-scope="props">{{ props.option.label }}: {{ props.option.number }}</template>
            </b-autocomplete>
          </div>
          <div class="control">
            <button type="button" class="button is-small signing-link-action" @click="onTextLink()" :class="{'is-loading': isTexting}" :disabled="!textMobile || isTexting">Text link</button>
          </div>
        </div>
        <button type="button" class="button is-small" @click="$emit('close')">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import QRCode from 'qrcode'
import { createNamespacedHelpers } from 'vuex'
import notificationService from '@/services/notificationService'
import customerService from '@/services/customerService'
import utilitiesMixin from '@/mixins/generic/utilities'
const mapDealerState = createNamespacedHelpers('dealer').mapState
const mapUserGetters = createNamespacedHelpers('user').mapGetters

// Shows an iPad signing link (see documentSigningLinkService) as a QR code, a copyable url and a text message.
export default {
  name: 'SigningLinkPanel',
  mixins: [utilitiesMixin],
  props: {
    link: {
      type: Object,
      required: true
    },
    defaultMobile: {
      type: String,
      default: ''
    },
    // { dealId, customerId, supplierId, content }; the ids are logged only when the text goes to one of the signer's numbers
    sms: {
      type: Object,
      default: () => ({})
    },
    // { id, name, mobile } of the deal's co-buyer, offered after the customer's own numbers
    coBuyer: {
      type: Object,
      default: null
    },
    waitingText: {
      type: String,
      default: 'Waiting for the customer to sign on the iPad...'
    }
  },
  data () {
    return {
      qrCode: '',
      textMobile: this.defaultMobile || '',
      numberOptions: [],
      isTexting: false
    }
  },
  computed: {
    ...mapUserGetters(['currentUser']),
    ...mapDealerState(['currentDealer']),
    expiryText: function () {
      return this.link ? moment(this.link.expiresAt).format('h:mm A') : ''
    }
  },
  methods: {
    renderQrCode: function () {
      // an image rather than a canvas ref, the panel can render inside a portal so the ref isn't there on the next tick
      this.qrCode = ''
      if (!this.link || !this.link.url) return
      QRCode.toDataURL(this.link.url, { width: 180, margin: 1 }).then(dataUrl => {
        this.qrCode = dataUrl
      }).catch((error) => {
        console.error('Error drawing the signing link QR code:', error)
      })
    },
    loadNumberOptions: function () {
      // a customer signer's other numbers to pick from, the default mobile first; a supplier signer keeps just the default
      this.numberOptions = []
      let customerId = this.sms.customerId
      if (!customerId) return
      let defaultMobile = this.defaultMobile
      customerService.single(customerId).then(response => {
        let customer = response && response.customer
        if (!customer || this.sms.customerId !== customerId) return
        let options = []
        let addOption = (label, number, optionCustomerId) => {
          let digits = (number || '').replace(/\D/g, '')
          if (digits === '' || options.find(x => x.number.replace(/\D/g, '') === digits)) return
          options.push({ label: label, number: number, customerId: optionCustomerId || customerId })
        }
        addOption('Mobile', defaultMobile)
        addOption('Mobile', customer.mobile)
        addOption('Home', customer.home)
        addOption('Office', customer.office)
        let additionalLabels = { CellPhone: 'Additional mobile', Voice: 'Additional phone', Home: 'Additional home', Office: 'Additional office' }
        let additionalNumbers = (customer.contactNumbers || []).filter(x => x.status === 'Active' && !x.isWrongProfile && additionalLabels[x.type])
        additionalNumbers.forEach(x => addOption(additionalLabels[x.type], x.contactNumber))
        if (this.coBuyer && this.coBuyer.mobile) {
          addOption('Co-buyer' + (this.coBuyer.name ? ' (' + this.coBuyer.name + ')' : ''), this.coBuyer.mobile, this.coBuyer.id)
        }
        this.numberOptions = options
      }).catch(() => {
        this.numberOptions = []
      })
    },
    onTextLink: function () {
      if (!this.textMobile) return
      // InteractiveTel needs a tracking number, the user's own before the dealer's
      let trackingNumber = (this.currentUser.activeTrackingNumber && this.currentUser.activeTrackingNumber !== '') ? this.currentUser.activeTrackingNumber : this.currentDealer.defaultCallTrackingNumber
      let pickedOption = this.numberOptions.find(x => x.number === this.textMobile)
      let toSigner = this.textMobile === this.defaultMobile || !!pickedOption
      let smsNotificationModel = {
        subscriberId: this.currentDealer.subscriberId,
        dealerId: this.currentDealer.id,
        mobileNo: this.textMobile,
        // logged against the customer (or co-buyer) or supplier when it goes to their own number, as other texts to them are
        customerId: toSigner ? ((pickedOption && pickedOption.customerId) || this.sms.customerId || null) : null,
        supplierId: toSigner ? (this.sms.supplierId || null) : null,
        content: (this.sms.content || 'Open this link to sign') + ': ' + this.link.url,
        callTrackingNumber: trackingNumber,
        smsType: 'Generic',
        dealId: this.sms.dealId || null,
        appointmentDate: null,
        canScheduleSms: true
      }
      this.isTexting = true
      notificationService.queueSms(smsNotificationModel).then(response => {
        this.isTexting = false
        if (response.errors) {
          this.failedToast('Oops! Something went wrong')
        } else {
          this.successToast('Success! Link texted to ' + this.textMobile + '.')
        }
      }).catch(() => {
        this.isTexting = false
        this.failedToast('Oops! Something went wrong')
      })
    }
  },
  watch: {
    link: function () {
      this.renderQrCode()
    },
    defaultMobile: function (newValue) {
      this.textMobile = newValue || ''
      this.loadNumberOptions()
    },
    'sms.customerId': function () {
      this.loadNumberOptions()
    }
  },
  mounted: function () {
    this.renderQrCode()
    this.loadNumberOptions()
    // the panel opens at the top of a long modal body, which is usually scrolled down to the footer buttons
    this.$nextTick(() => {
      if (this.$el && this.$el.scrollIntoView) {
        this.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    })
  }
}
</script>

<style scoped>
  /* the global input style is transparent, which shows the panel's blue through the link and number boxes */
  .signing-link-panel ::v-deep .input {
    background: #fff;
  }

  .signing-link-panel .signing-link-action {
    min-width: 6.5em;
  }
</style>
